import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import Input from '@lib/inputs/Input';
import RadioButtons from '@lib/inputs/RadioButtons';
import { GridStyledWrapper, StyledRadioButton } from '../voterRegistration.styled';
import useVoterRegistration from '../useVoterRegistration';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';

function VotingPollWorkerSection() {
    const { VOTER_REGISTRATION_CHOICES } = useVoterRegistration();
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<IVoterRegistrationFormValues>();
    const isBilingual = watch('pollWorker.isBilingual');

    const handleFieldChange = (name: string, value: string) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    return (
        <div id="pollWorker-section">
            <FormattedMessage id="voterRegistration.form.votingPollWorker.header" tagName="h2" />
            <AlertMessage
                type={'info'}
                content={
                    <FormattedMessage
                        id="voterRegistration.form.votingPollWorker.alert"
                        values={{ strong: (chunks: any) => <strong>{chunks}</strong> }}
                    />
                }
                showIcon={true}
                icon={'info-circle'}
            />
            <GridStyledWrapper maxWidth="50%">
                <span className="gi-radio_buttons">
                    <FormattedMessage id="voterRegistration.form.votingPollWorker.likeToBePollWorker" />
                </span>
                <StyledRadioButton>
                    <RadioButtons
                        legend={{
                            legendText: (
                                <FormattedMessage id="voterRegistration.form.votingPollWorker.likeToBePollWorker" />
                            ),
                        }}
                        label={{
                            isLabelVisible: false,
                            ulClass: ['d-flex gap-5'],
                        }}
                        inputData={{
                            name: 'pollWorker.isWorker',
                            id: 'pollWorker.isWorker',
                            options: VOTER_REGISTRATION_CHOICES.YES_NO,
                            type: 'radio_buttons',
                            value: watch('pollWorker.isWorker'),
                            inputRequired: true,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('pollWorker.isWorker', val),
                        }}
                    />
                </StyledRadioButton>
            </GridStyledWrapper>
            <VoterRegistrationFormError
                name="pollWorker.isWorker"
                hasError={!!errors?.pollWorker?.isWorker}
                messageId={errors?.pollWorker?.isWorker?.message}
            />
            <GridStyledWrapper maxWidth="50%">
                <span className="gi-radio_buttons">
                    <FormattedMessage id="voterRegistration.form.votingPollWorker.likeToBeBilingual" />
                </span>
                <StyledRadioButton>
                    <RadioButtons
                        legend={{
                            legendText: (
                                <FormattedMessage id="voterRegistration.form.votingPollWorker.likeToBeBilingual" />
                            ),
                        }}
                        label={{
                            isLabelVisible: false,
                            ulClass: ['d-flex gap-5'],
                        }}
                        inputData={{
                            name: 'pollWorker.isBilingual',
                            id: 'pollWorker.isBilingual',
                            options: VOTER_REGISTRATION_CHOICES.YES_NO,
                            type: 'radio_buttons',
                            value: isBilingual,
                            inputRequired: true,
                        }}
                        inputActions={{
                            handleFieldChange: val => {
                                handleFieldChange('pollWorker.isBilingual', val);
                                handleFieldChange('pollWorker.language', '');
                            },
                        }}
                    />
                </StyledRadioButton>
            </GridStyledWrapper>
            <VoterRegistrationFormError
                name="pollWorker.isBilingual"
                hasError={!!errors?.pollWorker?.isBilingual}
                messageId={errors?.pollWorker?.isBilingual?.message}
            />
            <ConditionalRendering isTrue={Boolean(isBilingual)}>
                <GridStyledWrapper>
                    <div>
                        <Input
                            label={{
                                text: <FormattedMessage id="voterRegistration.form.votingPollWorker.language" />,
                            }}
                            inputData={{
                                id: 'pollWorker.language',
                                name: 'pollWorker.language',
                                isInputRequired: true,
                                value: watch('pollWorker.language'),
                            }}
                            inputActions={{
                                handleFieldChange: val => handleFieldChange('pollWorker.language', val),
                            }}
                        />
                        <VoterRegistrationFormError
                            name="pollWorker.language"
                            hasError={!!errors?.pollWorker?.language}
                            messageId={errors?.pollWorker?.language?.message}
                        />
                    </div>
                </GridStyledWrapper>
            </ConditionalRendering>
        </div>
    );
}

export default VotingPollWorkerSection;
